import { Component, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ForwardingTarget, NotificationTarget, TargetPayload } from '../contacts.model';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSelectComponent,
  HlmSwitchComponent,
  HlmAlertComponent,
  HLM_FORM_FIELD,
  HlmSelectOption,
} from '../../../ui';

export type TargetKind = 'forwarding' | 'notification';
export type TargetMode = 'add' | 'edit';

export interface TargetDialogData {
  kind: TargetKind;
  mode: TargetMode;
  contactId: number;
  contactName: string;          // for the subtitle
  // Edit-mode prefill (omit when mode === 'add')
  existing?: ForwardingTarget | NotificationTarget;
}

interface MediaTypeOption { value: string; label: string; }

// Media-type options per notification type — sourced from old CMT constants.js
// EMAIL types: full HTML/Text/XML matrix
// SMS types: SINGLE / MULTIPART
// FAX type: TEXT (single option, auto-selected)
const MEDIA_TYPES_BY_TYPE: Record<string, ReadonlyArray<MediaTypeOption>> = {
  EMAIL: [
    { value: 'EMAIL_HTML', label: 'Email (HTML + Text)' },
    { value: 'EMAIL_TEXT', label: 'Email (Text only)' },
    { value: 'EMAIL_XML', label: 'Email (XML only)' },
    { value: 'EMAIL_HTML_AND_XML', label: 'Email (HTML + Text + XML)' },
    { value: 'EMAIL_HTML_AND_OPENIMMO', label: 'Email (HTML + XML OpenImmo)' },
  ],
  SMS: [
    { value: 'SINGLE', label: 'Single' },
    { value: 'MULTIPART', label: 'Multipart' },
  ],
  FAX: [
    { value: 'TEXT', label: 'Text' },
  ],
};

@Component({
  selector: 'cmt-target-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL,
    ...HLM_FORM_FIELD,
    HlmBtnDirective, HlmIconComponent, HlmInputDirective,
    HlmSelectComponent, HlmSwitchComponent, HlmAlertComponent,
  ],
  template: `
    <hlm-dialog-surface size="md">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon [name]="headerLucideIcon()" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>{{ headerTitle() }}</h2>
          <p hlmDialogDescription>{{ headerSubtitle() }}</p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        <form [formGroup]="form" (ngSubmit)="save()" class="flex flex-col gap-4">
          @if (errorMessage()) {
            <hlm-alert tone="error">
              <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
              <span>{{ errorMessage() }}</span>
            </hlm-alert>
          }

          <hlm-form-field>
            <label hlmLabel>Name</label>
            <input hlmInput formControlName="name" maxlength="50" />
          </hlm-form-field>

          <div class="grid grid-cols-[140px_1fr] gap-3">
            <hlm-form-field>
              <label hlmLabel>Type</label>
              <hlm-select
                [options]="typeSelectOptions()"
                [value]="form.controls.type.value"
                (valueChange)="form.controls.type.setValue($any($event) || '')"
              />
            </hlm-form-field>
            <hlm-form-field>
              <label hlmLabel>{{ valueLabel() }}</label>
              <input hlmInput formControlName="value" [placeholder]="valuePlaceholder()" />
            </hlm-form-field>
          </div>

          @if (data.kind === 'notification') {
            <hlm-form-field>
              <label hlmLabel>Media Type</label>
              <hlm-select
                [options]="mediaTypeSelectOptions()"
                [value]="form.controls.notificationMediaType.value"
                (valueChange)="form.controls.notificationMediaType.setValue($any($event) || '')"
              />
            </hlm-form-field>
          }

          <div class="flex items-center gap-6 py-1">
            <hlm-switch
              [checked]="form.controls.public.value"
              (checkedChange)="form.controls.public.setValue($any($event))"
              label="Public"
            />
            @if (data.kind === 'forwarding') {
              <hlm-switch
                [checked]="form.controls.emergency.value"
                (checkedChange)="form.controls.emergency.setValue($any($event))"
                label="Emergency"
              />
            } @else {
              <hlm-switch
                [checked]="form.controls.urgentOnly.value"
                (checkedChange)="form.controls.urgentOnly.setValue($any($event))"
                label="Urgent only"
              />
            }
          </div>
        </form>
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()">Cancel</button>
        <button hlmBtn (click)="save()" [disabled]="form.invalid">
          <hlm-icon name="lucideCheck" size="0.9rem" />
          {{ data.mode === 'edit' ? 'Save Changes' : (data.kind === 'forwarding' ? 'Add Forwarding Target' : 'Add Notification Target') }}
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class TargetDialogComponent {
  readonly dialogRef = inject(MatDialogRef<TargetDialogComponent, TargetPayload>);
  readonly data: TargetDialogData = inject(MAT_DIALOG_DATA);

  // Inline error display set by parent before re-opening / on retry
  readonly errorMessage = signal<string | null>(null);
  setError(msg: string | null): void { this.errorMessage.set(msg); }

  readonly typeOptions = computed<ReadonlyArray<MediaTypeOption>>(() =>
    this.data.kind === 'forwarding'
      ? [{ value: 'TELEPHONE', label: 'Telephone' }, { value: 'HANDY', label: 'Mobile' }]
      : [{ value: 'EMAIL', label: 'Email' }, { value: 'SMS', label: 'SMS' }, { value: 'FAX', label: 'Fax' }]
  );

  readonly valueLabel = computed(() => {
    if (this.data.kind === 'forwarding') return 'Phone Number';
    return this.typeSignal() === 'EMAIL' ? 'Email Address' : 'Phone Number';
  });

  readonly valuePlaceholder = computed(() => {
    if (this.data.kind === 'forwarding') return '+491701234567';
    return this.typeSignal() === 'EMAIL' ? 'test@example.com' : '+491701234567';
  });

  readonly headerIcon = computed(() => {
    if (this.data.kind === 'forwarding') return this.data.mode === 'edit' ? 'edit' : 'phone_forwarded';
    return this.data.mode === 'edit' ? 'edit' : 'notifications_active';
  });

  readonly headerLucideIcon = computed(() => {
    if (this.data.kind === 'forwarding') return this.data.mode === 'edit' ? 'lucidePencil' : 'lucidePhoneForwarded';
    return this.data.mode === 'edit' ? 'lucidePencil' : 'lucideBell';
  });

  readonly typeSelectOptions = computed<HlmSelectOption<string>[]>(() =>
    this.typeOptions().map(o => ({ value: o.value, label: o.label })),
  );

  readonly mediaTypeSelectOptions = computed<HlmSelectOption<string>[]>(() =>
    this.mediaTypeOptions().map(o => ({ value: o.value, label: o.label })),
  );

  readonly headerTitle = computed(() => {
    if (this.data.mode === 'edit') {
      return this.data.kind === 'forwarding' ? 'Edit Forwarding Target' : 'Edit Notification Target';
    }
    return this.data.kind === 'forwarding' ? 'Add Forwarding Target' : 'Add Notification Target';
  });

  readonly headerSubtitle = computed(() => `For contact: ${this.data.contactName}`);

  readonly form: FormGroup<{
    name: FormControl<string>;
    value: FormControl<string>;
    type: FormControl<string>;
    public: FormControl<boolean>;
    emergency: FormControl<boolean>;
    notificationMediaType: FormControl<string>;
    urgentOnly: FormControl<boolean>;
  }>;

  // Reactive type signal — used to drive media-type options + value placeholder
  private readonly typeSignal;

  // Reactive media-type list, depends on selected `type`
  readonly mediaTypeOptions = computed<ReadonlyArray<MediaTypeOption>>(() => {
    if (this.data.kind !== 'notification') return [];
    return MEDIA_TYPES_BY_TYPE[this.typeSignal()] ?? [];
  });

  constructor() {
    const isFwd = this.data.kind === 'forwarding';
    const existing = this.data.existing;
    const fwd = existing as ForwardingTarget | undefined;
    const notif = existing as NotificationTarget | undefined;

    const initialType = existing?.type ?? (isFwd ? 'TELEPHONE' : 'EMAIL');

    // Media-type required for notification, must default sensibly
    const initialMediaType = !isFwd
      ? (notif?.notificationMediaType ?? this.defaultMediaTypeFor(initialType))
      : '';

    this.form = new FormGroup({
      name: new FormControl(existing?.name ?? '', { nonNullable: true, validators: [Validators.required, Validators.maxLength(50)] }),
      value: new FormControl(existing?.value ?? '', { nonNullable: true, validators: [Validators.required] }),
      type: new FormControl(initialType, { nonNullable: true }),
      public: new FormControl(existing?.public ?? true, { nonNullable: true }),
      emergency: new FormControl(isFwd ? (fwd?.emergency ?? false) : false, { nonNullable: true }),
      notificationMediaType: new FormControl(initialMediaType, {
        nonNullable: true,
        validators: !isFwd ? [Validators.required] : [],
      }),
      urgentOnly: new FormControl(!isFwd ? (notif?.urgentOnly ?? false) : false, { nonNullable: true }),
    });

    // Reactive signal mirroring `type` form-control value
    this.typeSignal = toSignal(this.form.controls.type.valueChanges, { initialValue: initialType });

    // When type changes (only relevant for notification), reset media type to a valid default
    this.form.controls.type.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(newType => {
        if (this.data.kind !== 'notification') return;
        const allowed = MEDIA_TYPES_BY_TYPE[newType] ?? [];
        const current = this.form.controls.notificationMediaType.value;
        if (!allowed.find(o => o.value === current)) {
          this.form.controls.notificationMediaType.setValue(allowed[0]?.value ?? '');
        }
      });
  }

  /** First media-type option for a given target type — used as default when prefill missing */
  private defaultMediaTypeFor(type: string): string {
    return MEDIA_TYPES_BY_TYPE[type]?.[0]?.value ?? '';
  }

  save(): void {
    if (this.form.invalid) return;
    const v = this.form.getRawValue();
    const payload: TargetPayload = {
      contactId: this.data.contactId,
      name: v.name,
      value: v.value,
      type: v.type,
      public: v.public,
    };
    if (this.data.mode === 'edit' && this.data.existing) {
      payload.id = this.data.existing.id;
    }
    if (this.data.kind === 'forwarding') {
      payload.emergency = v.emergency;
    } else {
      payload.notificationMediaType = v.notificationMediaType || undefined;
      payload.urgentOnly = v.urgentOnly;
    }
    this.dialogRef.close(payload);
  }
}
